/**
 * Where the cameras sit relative to the transcript: above it, or beside it.
 *
 * Three settings, in the shape of theme.js: 'auto' - the default - plus the two
 * explicit choices the ⧉ button in the room bar switches between. Auto follows
 * the shape of the window, because the two arrangements suit different shapes:
 * a window wider than it is tall has room beside the transcript and none to
 * spare above it, and a tall one has the opposite.
 *
 * Unlike the theme, 'auto' resolves to a value here rather than being expressed
 * by leaving the attribute off for a media query to answer. It can: the stage
 * does not exist until the client has built it, so there is no first paint to
 * get wrong, and the arrangement is a two-way choice a query cannot express on
 * its own once a person is allowed to override it. What the stylesheet keeps is
 * the other half of the decision - whether the window is wide enough for the
 * choice to be offered at all, which is where the button appears and where the
 * side-by-side template is allowed to apply.
 */
const STORAGE_KEY = 'webchat.stage';

/** The settings a user can choose between. */
export const STAGE_LAYOUTS = ['auto', 'above', 'beside'];

/**
 * The shape at which cameras beside the transcript beats cameras above it.
 * Roughly 3:2 and wider: laptops, a phone held sideways, most desktop windows.
 */
const WIDE = '(min-aspect-ratio: 7/5)';

function read() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return STAGE_LAYOUTS.includes(stored) ? stored : 'auto';
  } catch {
    return 'auto';
  }
}

let preference = read();

/** The chosen setting: 'auto', 'above' or 'beside'. */
export function getStageLayout() {
  return preference;
}

/** What the shape of the window asks for. */
export function autoStageLayout() {
  if (typeof matchMedia !== 'function') return 'above';
  return matchMedia(WIDE).matches ? 'beside' : 'above';
}

/** The arrangement actually on screen, with 'auto' resolved. */
export function effectiveStageLayout() {
  return preference === 'auto' ? autoStageLayout() : preference;
}

/** Writes the resolved arrangement to the document for the stylesheet to read. */
export function applyStageLayout() {
  const layout = effectiveStageLayout();
  globalThis.document?.documentElement?.setAttribute('data-stage', layout);
  return layout;
}

/** Chooses an arrangement, remembers it, and applies it. */
export function setStageLayout(value) {
  if (!STAGE_LAYOUTS.includes(value)) return effectiveStageLayout();
  preference = value;
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* storage may be unavailable; the choice still applies to this page */
  }
  return applyStageLayout();
}

/**
 * Calls `listener` when the shape of the window crosses the threshold, but only
 * while the user is following it: someone who has picked an arrangement keeps it
 * whatever the window does.
 */
export function onAutoStageChange(listener) {
  if (typeof matchMedia !== 'function') return () => {};
  const query = matchMedia(WIDE);
  const handler = () => {
    if (preference === 'auto') listener(autoStageLayout());
  };
  query.addEventListener('change', handler);
  return () => query.removeEventListener('change', handler);
}

export default {
  STAGE_LAYOUTS,
  getStageLayout,
  setStageLayout,
  applyStageLayout,
  effectiveStageLayout,
  autoStageLayout,
  onAutoStageChange,
};
